import React, { useState } from 'react';
import type { FeeTotals, RevenueRow } from '../lib/api';

// ── Shared pieces for the Dashboard, Mentor Performance and Revenue pages ──
// One filter card scoping everything below it; KPI tiles that say what they
// count and whether they follow the period ("In period") or are as of today
// ("Now"); small charts with a tooltip on every bar and a table view. The
// definitions behind the numbers live in server/routes/reports.js.

// ── Colours ───────────────────────────────────────────────────────────────────
// For marks only — numbers and labels stay in ink. Status colours mean
// something (paid / owed / lost, attendance good / fair / low) and always come
// with a label.

export const C = {
  ink:      '#2b2240',
  bar:      '#4a3aa7',   // single-series bars
  muted:    '#c3c2b7',   // bars outside the period
  noShow:   '#898781',   // no-shows stacked on classes held
  good:     '#0ca30c',
  warning:  '#fab219',
  critical: '#d03b3b',
  phases:   ['#86b6ef', '#2a78d6', '#104281'],   // First 5 days → Mid course → Last 10 days
};

export type Tone = 'good' | 'warning' | 'critical' | 'neutral';

export const TONES: Record<Tone, { bg: string; fg: string; mark: string; icon: string }> = {
  good:     { bg: '#dcfce7', fg: '#166534', mark: C.good,     icon: '✓' },
  warning:  { bg: '#fef3c7', fg: '#92400e', mark: C.warning,  icon: '!' },
  critical: { bg: '#fee2e2', fg: '#b91c1c', mark: C.critical, icon: '✕' },
  neutral:  { bg: '#f3f4f6', fg: '#4b5563', mark: '#9ca3af',  icon: '•' },
};

// Attendance: 80%+ good, 60–79% fair, below 60% low
export function attTone(pct: number | null | undefined): Tone | null {
  if (pct == null) return null;
  return pct >= 80 ? 'good' : pct >= 60 ? 'warning' : 'critical';
}
export const ATT_LABEL: Record<Tone, string> = { good: 'Good', warning: 'Fair', critical: 'Low', neutral: '' };

export function attStatus(pct: number | null | undefined): { tone: Tone; label: string } | null {
  const tone = attTone(pct);
  return tone && { tone, label: ATT_LABEL[tone] };
}

// ── Numbers ───────────────────────────────────────────────────────────────────

export const num = (n: number) => Math.round(n || 0).toLocaleString('en-IN');
export const inr = (n: number) => '₹' + Math.round(n || 0).toLocaleString('en-IN');

// ₹1.6L, ₹65K, ₹1.2Cr
export function inrShort(n: number): string {
  const v = Math.abs(Math.round(n || 0));
  const short = (x: number) => (x >= 100 ? String(Math.round(x)) : x.toFixed(1).replace(/\.0$/, ''));
  const s = v >= 1e7 ? short(v / 1e7) + 'Cr' : v >= 1e5 ? short(v / 1e5) + 'L' : v >= 1e3 ? short(v / 1e3) + 'K' : String(v);
  return (n < 0 ? '−₹' : '₹') + s;
}

export const hours = (minutes: number) =>
  minutes < 60 ? `${Math.round(minutes)} min` : `${(minutes / 60).toFixed(minutes % 60 ? 1 : 0)} h`;

export const pctOf = (n: number, d: number) => (d ? Math.round((n / d) * 100) : null);

export const norm = (s: string | null | undefined) => String(s || '').trim().toLowerCase();

export const plural = (n: number, one: string, many = one + 's') => `${num(n)} ${n === 1 ? one : many}`;

// ── Dates (YYYY-MM-DD, IST) ───────────────────────────────────────────────────

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const todayIST = () => new Date(Date.now() + 5.5 * 60 * 60 * 1000).toISOString().slice(0, 10);
export const isDay = (s: string | null | undefined): s is string => !!s && /^\d{4}-\d{2}-\d{2}$/.test(s);

export function addDays(day: string, n: number) {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export const daysBetween = (a: string, b: string) =>
  Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400000);

function mondayOf(day: string) {
  const dow = new Date(`${day}T00:00:00Z`).getUTCDay();
  return addDays(day, dow === 0 ? -6 : 1 - dow);
}

// 'YYYY-MM' moved n months
export function shiftMonth(month: string, n: number) {
  const t = Number(month.slice(0, 4)) * 12 + Number(month.slice(5, 7)) - 1 + n;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}`;
}
const lastDayOf = (month: string) => addDays(`${shiftMonth(month, 1)}-01`, -1);

export const monthName = (month: string) => `${MONTHS[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;   // 'Oct 2026'

export function fmtDay(day: string, withYear = true) {
  if (!isDay(day)) return '—';
  return `${Number(day.slice(8, 10))} ${MONTHS[Number(day.slice(5, 7)) - 1]}${withYear ? ' ' + day.slice(0, 4) : ''}`;
}

export function fmtWeekday(day: string) {
  return `${['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date(`${day}T00:00:00Z`).getUTCDay()]} ${fmtDay(day)}`;
}

// '1–15 Oct 2026', '28 Sep – 4 Oct 2026', 'all time'
export function fmtRange(from: string, to: string) {
  if (!from) return `all time (up to ${fmtDay(to)})`;
  if (from === to) return fmtDay(from);
  if (from.slice(0, 7) === to.slice(0, 7)) return `${Number(from.slice(8, 10))}–${fmtDay(to)}`;
  if (from.slice(0, 4) === to.slice(0, 4)) return `${fmtDay(from, false)} – ${fmtDay(to)}`;
  return `${fmtDay(from)} – ${fmtDay(to)}`;
}

// "today", "yesterday", "12 days ago"
export function daysAgo(days: number | null) {
  if (days == null) return '—';
  return days === 0 ? 'today' : days === 1 ? 'yesterday' : `${num(days)} days ago`;
}

// ── Periods ───────────────────────────────────────────────────────────────────

export type PresetKey = 'today' | 'week' | 'month' | 'lastMonth' | '90d' | 'year' | 'all' | 'custom';
export interface Period { preset: PresetKey; from: string; to: string }   // from '' = all time

export const PRESET_LABEL: Record<PresetKey, string> = {
  today: 'Today', week: 'This week', month: 'This month', lastMonth: 'Last month',
  '90d': 'Last 90 days', year: 'This year', all: 'All time', custom: 'Custom dates',
};
const PRESET_ORDER: PresetKey[] = ['today', 'week', 'month', 'lastMonth', '90d', 'year', 'all', 'custom'];

export function presetPeriod(preset: PresetKey, today = todayIST()): Period {
  switch (preset) {
    case 'today':     return { preset, from: today, to: today };
    case 'week':      return { preset, from: mondayOf(today), to: today };
    case 'month':     return { preset, from: `${today.slice(0, 8)}01`, to: today };
    case 'lastMonth': { const m = shiftMonth(today.slice(0, 7), -1); return { preset, from: `${m}-01`, to: lastDayOf(m) }; }
    case '90d':       return { preset, from: addDays(today, -89), to: today };
    case 'year':      return { preset, from: `${today.slice(0, 4)}-01-01`, to: today };
    case 'all':       return { preset, from: '', to: today };
    default:          return { preset: 'custom', from: addDays(today, -29), to: today };
  }
}

// What to compare a period with: the same stretch one step back — this month
// so far against the same days last month, this week against last week…
export function previousPeriod(p: Period): { from: string; to: string } | null {
  if (!p.from) return null;
  switch (p.preset) {
    case 'today': return { from: addDays(p.from, -1), to: addDays(p.to, -1) };
    case 'week':  return { from: addDays(p.from, -7), to: addDays(p.to, -7) };
    case 'month': {
      const m = shiftMonth(p.from.slice(0, 7), -1);
      const to = `${m}-${p.to.slice(8, 10)}`;
      return { from: `${m}-01`, to: to > lastDayOf(m) ? lastDayOf(m) : to };
    }
    case 'lastMonth': { const m = shiftMonth(p.from.slice(0, 7), -1); return { from: `${m}-01`, to: lastDayOf(m) }; }
    case 'year': {
      const y = Number(p.from.slice(0, 4)) - 1;
      const to = `${y}${p.to.slice(4)}`;
      return { from: `${y}-01-01`, to: to.endsWith('-02-29') ? `${y}-02-28` : to };
    }
    default: {
      const n = daysBetween(p.from, p.to) + 1;
      return { from: addDays(p.from, -n), to: addDays(p.from, -1) };
    }
  }
}

export const within = (day: string, p: { from: string; to: string }) => !!day && day >= p.from && day <= p.to;

// The period in the page address (?p=month, or ?p=custom&from=…&to=…; an
// empty from is "up to `to`", see PeriodPicker's dates)
export function periodFromParams(params: URLSearchParams, fallback: PresetKey): Period {
  const p = params.get('p') as PresetKey | null;
  const from = params.get('from'), to = params.get('to');
  if (p === 'custom' && (from === '' || isDay(from)) && isDay(to) && from <= to) return { preset: 'custom', from, to };
  if (p && p !== 'custom' && PRESET_ORDER.includes(p)) return presetPeriod(p);
  return presetPeriod(fallback);
}

export const periodParams = (p: Period): Record<string, string> =>
  p.preset === 'custom' ? { p: 'custom', from: p.from, to: p.to } : { p: p.preset };

// ── Comparisons ───────────────────────────────────────────────────────────────

export interface Delta { text: string; tone: 'good' | 'bad' | 'neutral'; vs: string }

// ▲ / ▼ against the compared period; upIsGood says which way is better
export function delta(
  cur: number | null | undefined, prev: number | null | undefined, vs: string,
  opts: { upIsGood?: boolean; format?: (n: number) => string; unit?: string } = {}
): Delta | null {
  if (cur == null || prev == null || !vs) return null;
  const diff = cur - prev;
  if (Math.abs(diff) < 0.05) return { text: 'Same', tone: 'neutral', vs };
  const { upIsGood = true, format = num, unit = '' } = opts;
  return { text: `${diff > 0 ? '▲' : '▼'} ${format(Math.abs(diff))}${unit}`, tone: (diff > 0) === upIsGood ? 'good' : 'bad', vs };
}

// ── Fees (same rules as feeTotals in server/routes/reports.js) ────────────────

export function feeTotals(rows: RevenueRow[]): FeeTotals {
  const t: FeeTotals = { students: rows.length, collected: 0, outstanding: 0, lost: 0, total: 0, regCollected: 0, fullyPaid: 0, owing: 0, lostStudents: 0, feeNotSet: 0 };
  rows.forEach(r => {
    t.collected    += r.totalPaid;
    t.regCollected += r.regPaid;
    if (r.payStatus === 'Ran Away') { t.lost += r.balance; t.lostStudents++; }
    else if (r.payStatus === 'Fee not set') t.feeNotSet++;
    else if (r.payStatus === 'Fully Paid') t.fullyPaid++;
    else { t.outstanding += r.balance; t.owing++; }
  });
  t.total = t.collected + t.outstanding + t.lost;
  return t;
}

// Distinct names — case and spacing ignored — each in its most used spelling
export function distinctNames(values: string[]): string[] {
  const spellings: Record<string, Record<string, number>> = {};
  values.forEach(raw => {
    const name = String(raw || '').trim();
    if (!name) return;
    const k = norm(name);
    spellings[k] = spellings[k] || {};
    spellings[k][name] = (spellings[k][name] || 0) + 1;
  });
  return Object.keys(spellings)
    .map(k => Object.entries(spellings[k]).sort((a, b) => b[1] - a[1])[0][0])
    .sort((a, b) => a.localeCompare(b));
}

// ── CSV ───────────────────────────────────────────────────────────────────────

export function downloadCsv(filename: string, header: string[], rows: (string | number)[][]) {
  const cell = (v: string | number) => {
    if (typeof v === 'number') return String(v);
    let s = String(v ?? '');
    if (/^[=+\-@]/.test(s)) s = `'${s}`;   // so spreadsheets don't read it as a formula
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [header, ...rows].map(r => r.map(cell).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── Page header ───────────────────────────────────────────────────────────────

export const ReportHeader: React.FC<{ icon: string; title: string; intro: React.ReactNode; actions?: React.ReactNode }> = ({ icon, title, intro, actions }) => (
  <div className="rounded-2xl overflow-hidden shadow-sm" style={{ background: 'linear-gradient(135deg,#1e1730 0%,#2b2240 55%,#3d2f5e 100%)' }}>
    <div className="px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-2xl">{icon}</span>
          <h1 className="text-xl font-black text-white">{title}</h1>
        </div>
        <p className="text-xs text-white/60 max-w-2xl leading-relaxed">{intro}</p>
      </div>
      {actions && <div className="flex items-center gap-2 self-start sm:self-auto">{actions}</div>}
    </div>
  </div>
);

export const HeaderButton: React.FC<{ onClick: () => void; children: React.ReactNode; disabled?: boolean }> = ({ onClick, children, disabled }) => (
  <button onClick={onClick} disabled={disabled}
    className="px-3 py-2 rounded-xl text-xs font-medium border border-white/20 text-white/80 hover:bg-white/10 transition-colors disabled:opacity-40 whitespace-nowrap">
    {children}
  </button>
);

// ── Filters ───────────────────────────────────────────────────────────────────

const Caption: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1.5">{children}</span>
);

const chipStyle = (on: boolean): React.CSSProperties => on
  ? { background: C.ink, color: '#fff', borderColor: C.ink }
  : { background: '#fff', color: '#6b7280', borderColor: '#e5e7eb' };

// dates: From and To always showing, rather than behind "Custom dates". Either
// can be left empty — no From is "up to To", no To is "up to today".
export const PeriodPicker: React.FC<{ label: string; value: Period; onChange: (p: Period) => void; presets?: PresetKey[]; dates?: boolean }> = ({ label, value, onChange, presets = PRESET_ORDER, dates }) => {
  const today = todayIST();
  const pick = (k: PresetKey) => onChange(k === 'custom'
    ? { preset: 'custom', from: value.from || addDays(today, -29), to: value.to || today }
    : presetPeriod(k, today));
  const range = (from: string, to: string) => onChange(!from && to === today ? presetPeriod('all', today) : { preset: 'custom', from, to });
  const setFrom = (v: string) => { if (!v) range('', value.to); else if (isDay(v)) range(v, value.to < v ? v : value.to); };
  const setTo   = (v: string) => { if (!v) range(value.from, today); else if (isDay(v)) range(value.from > v ? v : value.from, v); };
  const dateCls = 'px-3 py-1.5 border-2 rounded-xl text-xs focus:outline-none focus:border-[#2b2240]';
  return (
    <div>
      <Caption>{label}</Caption>
      <div className="flex flex-wrap gap-1.5">
        {presets.map(k => (
          <button key={k} type="button" onClick={() => pick(k)} aria-pressed={value.preset === k}
            className="px-3 py-1.5 rounded-xl text-xs font-bold border-2 transition-colors" style={chipStyle(value.preset === k)}>
            {PRESET_LABEL[k]}
          </button>
        ))}
      </div>
      {dates ? (
        <div className="flex flex-wrap items-end gap-2 mt-2">
          <label className="flex flex-col">
            <span className="text-[10px] font-semibold text-gray-400 mb-1">From</span>
            <input type="date" value={value.from} max={value.to || today} onChange={e => setFrom(e.target.value)}
              className={dateCls} style={{ borderColor: value.from ? C.ink : '#e5e7eb' }} />
          </label>
          <label className="flex flex-col">
            <span className="text-[10px] font-semibold text-gray-400 mb-1">To</span>
            <input type="date" value={value.to} min={value.from || undefined} max={today} onChange={e => setTo(e.target.value)}
              className={dateCls} style={{ borderColor: value.preset !== 'all' ? C.ink : '#e5e7eb' }} />
          </label>
        </div>
      ) : value.preset === 'custom' && (
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <input type="date" value={value.from} max={value.to || today}
            onChange={e => isDay(e.target.value) && onChange({ preset: 'custom', from: e.target.value, to: value.to < e.target.value ? e.target.value : value.to })}
            className="px-3 py-1.5 border-2 border-gray-200 rounded-xl text-xs focus:outline-none focus:border-[#2b2240]" />
          <span className="text-xs text-gray-400">to</span>
          <input type="date" value={value.to} min={value.from} max={today}
            onChange={e => isDay(e.target.value) && onChange({ preset: 'custom', from: value.from > e.target.value ? e.target.value : value.from, to: e.target.value })}
            className="px-3 py-1.5 border-2 border-gray-200 rounded-xl text-xs focus:outline-none focus:border-[#2b2240]" />
        </div>
      )}
    </div>
  );
};

export interface Option { value: string; label: string }

export const SelectFilter: React.FC<{ label: string; value: string; onChange: (v: string) => void; options: Option[]; allLabel: string }> = ({ label, value, onChange, options, allLabel }) => (
  <label className="flex flex-col min-w-[140px] flex-1 sm:flex-none">
    <Caption>{label}</Caption>
    <select value={value} onChange={e => onChange(e.target.value)}
      className="px-3 py-2 border-2 rounded-xl text-xs font-semibold bg-white focus:outline-none"
      style={{ borderColor: value ? C.ink : '#e5e7eb', color: value ? C.ink : '#6b7280' }}>
      <option value="">{allLabel}</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </label>
);

export const SearchFilter: React.FC<{ label: string; value: string; onChange: (v: string) => void; placeholder: string }> = ({ label, value, onChange, placeholder }) => (
  <label className="flex flex-col flex-1 min-w-[200px]">
    <Caption>{label}</Caption>
    <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="px-3 py-2 border-2 rounded-xl text-xs bg-white focus:outline-none focus:border-[#2b2240]"
      style={{ borderColor: value ? C.ink : '#e5e7eb' }} />
  </label>
);

// A filter that's on or off (several can be on at once)
export const ToggleChip: React.FC<{ on: boolean; onClick: () => void; children: React.ReactNode; count?: number; title?: string }> = ({ on, onClick, children, count, title }) => (
  <button type="button" onClick={onClick} aria-pressed={on} title={title}
    className="px-3 py-1.5 rounded-xl text-xs font-bold border-2 transition-colors whitespace-nowrap" style={chipStyle(on)}>
    {children}{count !== undefined && <span className="ml-1 opacity-70">({num(count)})</span>}
  </button>
);

export interface ActiveFilter { label: string; onRemove: () => void }

// The filter card: controls on top, then what's showing and every filter
// that's on, each removable
export const FilterCard: React.FC<{
  children: React.ReactNode; showing: React.ReactNode; active: ActiveFilter[]; onClearAll?: () => void; busy?: boolean;
}> = ({ children, showing, active, onClearAll, busy }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-4">
    {children}
    <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-gray-100">
      <p className="text-xs text-gray-500 mr-1">{showing}</p>
      {active.map(f => (
        <span key={f.label} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 rounded-full text-[11px] font-semibold" style={{ background: '#ede9fe', color: '#4c1d95' }}>
          {f.label}
          <button onClick={f.onRemove} aria-label={`Remove filter ${f.label}`} className="w-4 h-4 rounded-full hover:bg-white/70 leading-none">×</button>
        </span>
      ))}
      {onClearAll && active.length > 0 && (
        <button onClick={onClearAll} className="text-[11px] font-bold text-gray-500 hover:text-gray-800 underline underline-offset-2">Clear all</button>
      )}
      {busy && <span className="ml-auto w-4 h-4 rounded-full border-2 border-purple-200 border-t-purple-600 animate-spin" aria-label="Updating" />}
    </div>
  </div>
);

// ── Layout ────────────────────────────────────────────────────────────────────

// A <div>, not a <section>: the website's mobile CSS pads every <section> by 60px
export const Section: React.FC<{ title: string; caption?: React.ReactNode; action?: React.ReactNode; children: React.ReactNode; id?: string }> = ({ title, caption, action, children, id }) => (
  <div className="space-y-3" id={id}>
    <div className="flex flex-wrap items-end justify-between gap-2">
      <div>
        <h2 className="text-sm font-black uppercase tracking-wider" style={{ color: C.ink }}>{title}</h2>
        {caption && <p className="text-xs text-gray-400 mt-0.5">{caption}</p>}
      </div>
      {action}
    </div>
    {children}
  </div>
);

export const Card: React.FC<{ title?: React.ReactNode; subtitle?: React.ReactNode; right?: React.ReactNode; children: React.ReactNode; className?: string; flush?: boolean }> = ({ title, subtitle, right, children, className = '', flush }) => (
  <div className={`bg-white rounded-2xl shadow-sm border border-gray-100 ${flush ? '' : 'p-5'} ${className}`}>
    {(title || right) && (
      <div className={`flex flex-wrap items-start justify-between gap-2 ${flush ? 'px-5 pt-4 pb-3' : 'mb-4'}`}>
        <div>
          {title && <p className="text-sm font-bold text-gray-800">{title}</p>}
          {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
        {right}
      </div>
    )}
    {children}
  </div>
);

export const LinkButton: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <button onClick={onClick} className="text-xs font-bold px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 whitespace-nowrap">
    {children}
  </button>
);

export const Pill: React.FC<{ tone: Tone; children: React.ReactNode; title?: string; icon?: boolean }> = ({ tone, children, title, icon = true }) => (
  <span title={title} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap"
    style={{ background: TONES[tone].bg, color: TONES[tone].fg }}>
    {icon && <span aria-hidden>{TONES[tone].icon}</span>}{children}
  </span>
);

export const ScopeTag: React.FC<{ scope: 'now' | 'period' }> = ({ scope }) => (
  <span title={scope === 'now' ? "As of today — doesn't change with the period" : 'Follows the period you picked'}
    className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md whitespace-nowrap"
    style={scope === 'now' ? { background: '#f3f4f6', color: '#6b7280' } : { background: '#ede9fe', color: '#5b21b6' }}>
    {scope === 'now' ? 'Now' : 'In period'}
  </span>
);

// ── KPI tile ──────────────────────────────────────────────────────────────────

export const KpiTile: React.FC<{
  label: string; value: React.ReactNode; hint: React.ReactNode; scope?: 'now' | 'period';
  delta?: Delta | null; status?: { tone: Tone; label: string } | null;
  onClick?: () => void; cta?: string;
}> = ({ label, value, hint, scope, delta: d, status, onClick, cta }) => {
  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-semibold text-gray-600 leading-snug">{label}</p>
        {scope && <ScopeTag scope={scope} />}
      </div>
      <div className="flex items-center flex-wrap gap-x-2 gap-y-1">
        <p className="text-2xl font-black leading-tight" style={{ color: C.ink }}>{value}</p>
        {status && <Pill tone={status.tone}>{status.label}</Pill>}
      </div>
      {d && (
        <p className="text-[11px] font-semibold" style={{ color: d.tone === 'good' ? '#15803d' : d.tone === 'bad' ? '#b91c1c' : '#6b7280' }}>
          {d.text} <span className="font-normal text-gray-400">vs {d.vs}</span>
        </p>
      )}
      <p className="text-[11px] text-gray-400 leading-snug">{hint}</p>
      {onClick && cta && <p className="text-[11px] font-bold mt-auto pt-1" style={{ color: C.ink }}>{cta} →</p>}
    </>
  );
  const cls = 'text-left bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex flex-col gap-1.5 h-full';
  return onClick
    ? <button onClick={onClick} className={`${cls} hover:shadow-md hover:border-gray-300 transition-all`}>{body}</button>
    : <div className={cls}>{body}</div>;
};

// ── "How are these counted?" ──────────────────────────────────────────────────

export const HowCounted: React.FC<{ items: [string, React.ReactNode][] }> = ({ items }) => (
  <details className="bg-white rounded-2xl border border-gray-100 shadow-sm group">
    <summary className="px-5 py-3.5 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden flex items-center justify-between gap-2">
      <span className="text-xs font-bold text-gray-600">ⓘ How are these numbers counted?</span>
      <span className="text-gray-400 text-xs transition-transform group-open:rotate-180">▼</span>
    </summary>
    <dl className="px-5 pb-5 grid sm:grid-cols-2 gap-x-8 gap-y-3">
      {items.map(([term, def]) => (
        <div key={term}>
          <dt className="text-xs font-bold text-gray-700">{term}</dt>
          <dd className="text-xs text-gray-500 leading-relaxed mt-0.5">{def}</dd>
        </div>
      ))}
    </dl>
  </details>
);

// ── Charts ────────────────────────────────────────────────────────────────────

export interface ChartSeries { name: string; color: string }
export interface ColumnPoint { key: string; label: string; title: string; values: number[]; muted?: boolean }

const Legend: React.FC<{ items: ChartSeries[] }> = ({ items }) => (
  <div className="flex flex-wrap gap-x-4 gap-y-1">
    {items.map(s => (
      <span key={s.name} className="inline-flex items-center gap-1.5 text-[11px] text-gray-500">
        <span className="w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />{s.name}
      </span>
    ))}
  </div>
);

// Rounds a chart's top up to 2, 4, 6, 8 or 10 × 10ⁿ, so the halfway line is a round number too
function niceTop(v: number) {
  if (v <= 2) return 2;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  const step = [1, 2, 4, 6, 8, 10].find(s => s * p >= v) || 10;
  return step * p;
}

// Columns, stacked when there's more than one series. Every column has a
// tooltip (hover or keyboard focus); the highest and the latest carry their
// total; "Show as table" lists every value.
export const ColumnChart: React.FC<{
  points: ColumnPoint[]; series: ChartSeries[]; firstColumn: string;
  format?: (n: number) => string; axisFormat?: (n: number) => string;
  note?: (p: ColumnPoint) => string | null; noteLabel?: string;
  height?: number; emptyText?: string; legendExtra?: React.ReactNode;
}> = ({ points, series, firstColumn, format = num, axisFormat, note, noteLabel, height = 150, emptyText = 'Nothing to show for this period', legendExtra }) => {
  const [hover, setHover] = useState<number | null>(null);
  const [asTable, setAsTable] = useState(false);
  const totals = points.map(p => p.values.reduce((a, b) => a + b, 0));
  const top = niceTop(Math.max(0, ...totals));
  const n = points.length;
  const every = Math.max(1, Math.ceil(n / 10));
  const peak = totals.indexOf(Math.max(...totals));
  const labelled = new Set([peak, n - 1].filter(i => i >= 0 && totals[i] > 0));
  const empty = totals.every(t => t === 0);
  const axis = axisFormat || format;
  const hp = hover === null ? null : points[hover];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          {series.length > 1 && <Legend items={series} />}
          {legendExtra}
        </div>
        <button onClick={() => setAsTable(v => !v)} className="text-[11px] font-semibold text-gray-400 hover:text-gray-700 underline underline-offset-2">
          {asTable ? 'Show chart' : 'Show as table'}
        </button>
      </div>

      {asTable ? (
        <div className="max-h-72 overflow-auto rounded-xl border border-gray-100">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-gray-500">{firstColumn}</th>
                {series.map(s => <th key={s.name} className="px-3 py-2 text-right font-semibold text-gray-500">{s.name}</th>)}
                {series.length > 1 && <th className="px-3 py-2 text-right font-semibold text-gray-500">Total</th>}
                {note && <th className="px-3 py-2 text-right font-semibold text-gray-500">{noteLabel}</th>}
              </tr>
            </thead>
            <tbody>
              {points.map((p, i) => (
                <tr key={p.key} className="border-t border-gray-50">
                  <td className="px-3 py-1.5 text-gray-700">{p.title}</td>
                  {p.values.map((v, k) => <td key={k} className="px-3 py-1.5 text-right tabular-nums text-gray-700">{format(v)}</td>)}
                  {series.length > 1 && <td className="px-3 py-1.5 text-right tabular-nums font-bold text-gray-800">{format(totals[i])}</td>}
                  {note && <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">{note(p) ?? '—'}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="relative pl-10">
          {/* gridlines */}
          <div className="absolute left-0 right-0" style={{ top: 16, height }}>
            {[1, 0.5, 0].map(f => (
              <div key={f} className="absolute left-0 right-0 flex items-center" style={{ top: `${(1 - f) * 100}%`, transform: 'translateY(-50%)' }}>
                <span className="w-10 pr-2 text-right text-[10px] text-gray-400 tabular-nums">{axis(top * f)}</span>
                <span className="flex-1 h-px" style={{ background: f === 0 ? '#d1d5db' : '#f1f1f4' }} />
              </div>
            ))}
          </div>

          {/* columns */}
          <div className="relative flex items-end" style={{ height: height + 16 }}>
            {points.map((p, i) => {
              const parts = p.values.map((v, k) => ({ v, color: p.muted ? C.muted : series[k].color })).filter(x => x.v > 0);
              return (
                <div key={p.key} tabIndex={0} role="img"
                  aria-label={`${p.title}: ${series.map((s, k) => `${format(p.values[k])} ${s.name}`).join(', ')}`}
                  onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
                  onFocus={() => setHover(i)} onBlur={() => setHover(null)}
                  className="flex-1 h-full flex flex-col justify-end items-center outline-none focus-visible:bg-purple-50 rounded">
                  {labelled.has(i) && hover === null && (
                    <span className="text-[10px] font-bold text-gray-700 tabular-nums mb-0.5 whitespace-nowrap">{format(totals[i])}</span>
                  )}
                  <div className="flex flex-col-reverse gap-[2px]" style={{ width: '70%', maxWidth: 24, filter: hover === i ? 'brightness(1.2)' : undefined }}>
                    {parts.map((part, k) => (
                      <div key={k} style={{
                        height: Math.max(2, (part.v / top) * height - (k > 0 ? 2 : 0)),
                        background: part.color,
                        borderRadius: k === parts.length - 1 ? '4px 4px 0 0' : 0,
                      }} />
                    ))}
                  </div>
                </div>
              );
            })}
            {empty && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="text-xs text-gray-400 bg-white/80 px-2 py-1 rounded">{emptyText}</span>
              </div>
            )}
            {hp && hover !== null && (
              <div className="absolute z-20 pointer-events-none bg-white rounded-xl shadow-lg border border-gray-100 px-3 py-2 text-xs min-w-[150px]"
                style={{
                  top: 0,
                  left: `${((hover + 0.5) / n) * 100}%`,
                  transform: `translateX(${hover / n < 0.25 ? '-12%' : hover / n > 0.75 ? '-88%' : '-50%'})`,
                }}>
                <p className="font-bold text-gray-800 mb-1 whitespace-nowrap">{hp.title}</p>
                {series.map((s, k) => (
                  <div key={s.name} className="flex items-center gap-2 whitespace-nowrap">
                    <span className="inline-block w-3 h-[3px] rounded-full" style={{ background: hp.muted ? C.muted : s.color }} />
                    <span className="font-bold text-gray-800 tabular-nums">{format(hp.values[k])}</span>
                    <span className="text-gray-400">{s.name}</span>
                  </div>
                ))}
                {note && note(hp) && <p className="text-gray-500 mt-1 whitespace-nowrap">{note(hp)}</p>}
              </div>
            )}
          </div>

          {/* x labels */}
          <div className="flex mt-1.5">
            {points.map((p, i) => {
              // counted back from the latest column, so it always has a label;
              // half as many on phones, so they don't run into each other
              const back = n - 1 - i;
              return (
                <div key={p.key} className="flex-1 min-w-0 flex justify-center text-[10px] text-gray-400 whitespace-nowrap">
                  {back % every === 0 && <span className={(back / every) % 2 === 1 ? 'hidden sm:inline' : ''}>{p.label}</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// One bar split into parts, with a legend giving each part's value and share
export const StackBar: React.FC<{
  parts: { label: string; value: number; color: string; hint?: string }[];
  format?: (n: number) => string; columns?: string;
}> = ({ parts, format = num, columns = 'grid-cols-2 sm:grid-cols-4' }) => {
  const total = parts.reduce((s, p) => s + p.value, 0);
  return (
    <div>
      <div className="flex h-3 w-full gap-[2px] rounded overflow-hidden bg-gray-100">
        {parts.filter(p => p.value > 0).map(p => (
          <div key={p.label} title={`${p.label}: ${format(p.value)} (${pctOf(p.value, total)}%)`}
            style={{ flex: `${p.value} 1 0%`, minWidth: 3, background: p.color }} />
        ))}
      </div>
      <div className={`grid ${columns} gap-3 mt-3`}>
        {parts.map(p => (
          <div key={p.label} className="flex items-start gap-2 min-w-0">
            <span className="mt-1 w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: p.color }} />
            <div className="min-w-0">
              <p className="text-[11px] text-gray-500">{p.label}</p>
              <p className="text-sm font-bold text-gray-800 tabular-nums">
                {format(p.value)}
                {total > 0 && <span className="text-[11px] font-semibold text-gray-400"> · {pctOf(p.value, total)}%</span>}
              </p>
              {p.hint && <p className="text-[10px] text-gray-400 leading-snug">{p.hint}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// A small filled track for a percentage in a table cell
export const MiniMeter: React.FC<{ pct: number | null; color: string; width?: number; title?: string }> = ({ pct, color, width = 56, title }) => (
  <span className="inline-flex items-center gap-2" title={title}>
    <span className="relative h-1.5 rounded-full bg-gray-100 overflow-hidden flex-shrink-0" style={{ width }}>
      {pct != null && <span className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${Math.min(100, Math.max(0, pct))}%`, background: color }} />}
    </span>
    <span className="text-xs font-bold tabular-nums text-gray-700 w-9">{pct == null ? '—' : `${pct}%`}</span>
  </span>
);

// Attendance in a table cell: track + number, coloured good / fair / low
export const AttendanceCell: React.FC<{ pct: number | null }> = ({ pct }) => {
  const tone = attTone(pct);
  return <MiniMeter pct={pct} color={tone ? TONES[tone].mark : '#e5e7eb'} title={tone ? `${ATT_LABEL[tone]} attendance` : 'No classes'} />;
};

// ── States ────────────────────────────────────────────────────────────────────

export const Loading: React.FC<{ text: string }> = ({ text }) => (
  <div className="flex flex-col items-center justify-center h-48 gap-3">
    <div className="w-8 h-8 rounded-full border-4 border-purple-200 border-t-purple-600 animate-spin" />
    <p className="text-sm text-gray-400">{text}</p>
  </div>
);

export const ServerOutdated: React.FC = () => (
  <div className="rounded-2xl border-2 border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900">
    <p className="font-bold">The server hasn't been updated yet</p>
    <p className="text-xs mt-1 text-amber-800">This page needs the latest server. Deploy the server, then press Refresh.</p>
  </div>
);

export const Notice: React.FC<{ tone: Tone; children: React.ReactNode; action?: React.ReactNode }> = ({ tone, children, action }) => (
  <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl px-4 py-2.5 text-xs" style={{ background: TONES[tone].bg, color: TONES[tone].fg }}>
    <p className="flex items-start gap-1.5"><span aria-hidden className="font-black">{TONES[tone].icon}</span><span>{children}</span></p>
    {action}
  </div>
);
